import apiClient from '../client';
import ENDPOINTS from '../endpoints';
import { normalizeNoSchoolDay } from './noSchool.service';

const pad = (n) => String(n).padStart(2, '0');

/* The list unions NoSchool notes (a day off set on the Master Calendar) in
   with the network's events — tagged itemType 'note', and eventType is the
   note kind for older rows the tag is missing on. */
const isDayOff = (e) => e?.itemType === 'note' || e?.eventType === 'NoSchool';

/* eventStart/eventEnd are UTC ISO strings — split back into the local date and
   HH:MM times the calendar UI renders. */
function normalizeEvent(e) {
  const start = e?.eventStart ? new Date(e.eventStart) : null;
  const end = e?.eventEnd ? new Date(e.eventEnd) : null;
  const rsvps = e?.rsvps || e?.attendees || [];
  return {
    id: e?._id || e?.id || '',
    /* the event's own string id — what the to-do API groups tasks by, and not
       the same value as `id` above (that one is the Mongo _id) */
    eventId: e?.eventId || '',
    title: e?.eventName || e?.title || '',
    date: start ? `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}` : '',
    startTime: start ? `${pad(start.getHours())}:${pad(start.getMinutes())}` : '',
    endTime: end ? `${pad(end.getHours())}:${pad(end.getMinutes())}` : '',
    desc: e?.description || e?.desc || '',
    /* the image URL lives in coverUrl or coverImg depending on how the cover
       was set (upload vs unsplash) — take whichever holds a value */
    image: e?.coverUrl || e?.coverImg || e?.image || null,
    locationName: e?.locationName || '',
    /* status true = already sent to parents (Send to Parents button goes away) */
    status: e?.status === true || e?.status === 'true',
    isEmailSent: e?.isEmailSent === true || e?.isEmailSent === 'true',
    eventType: e?.eventType || '',
    /* the share link the backend minted for this event — what goes in emails */
    publicURL: e?.publicURL || '',
    going: Number(
      e?.going ?? e?.goingCount ?? e?.totalGoing ?? e?.membersGoing ??
      (Array.isArray(rsvps)
        ? rsvps.filter((r) => !r || r.response === 'yes' || r.status === 'going' || r.status === 'accepted').length
        : 0)
    ) || 0,
    spots: e?.spots ?? '',
  };
}

const eventsService = {
  /* One server page of a network's calendar. A school (withNoSchool) gets its
     events unioned with its days off, upcoming first, returned as two lists —
     `events` and `noSchoolDays` — with total and totalPages counting both. A
     class or sub group uses the plain events list and gets no days off. */
  listEvents: async (networkId, { page = 1, limit = 10, withNoSchool = false } = {}) => {
    const url = withNoSchool
      ? ENDPOINTS.ROOM_PARENT_ADMIN.EVENTS_WITH_NO_SCHOOL_BY_NETWORK(networkId)
      : ENDPOINTS.ROOM_PARENT_ADMIN.EVENTS_BY_NETWORK(networkId);
    const res = await apiClient.get(url, { params: { page, limit } });
    const data = res?.data ?? res;
    const rawList =
      data?.events || data?.docs || data?.items || data?.results || data?.list || (Array.isArray(data) ? data : []);
    const total = Number(data?.total ?? data?.totalCount ?? data?.count ?? data?.pagination?.total ?? rawList.length);
    const totalPages = Number(
      data?.totalPages ?? data?.pages ?? data?.pagination?.totalPages ?? Math.max(1, Math.ceil(total / limit))
    );
    return {
      events: rawList.filter((e) => !isDayOff(e)).map(normalizeEvent),
      noSchoolDays: rawList.filter(isDayOff).map(normalizeNoSchoolDay),
      total,
      totalPages,
    };
  },

  /* Address/place lookup (Google Places proxied by the backend, public route).
     Returns [{ name, address, lat, lng }]. */
  searchAddress: async (search) => {
    const res = await apiClient.post(ENDPOINTS.PUBLIC.SEARCH_ADDRESS, { search });
    const results = res?.data?.data?.results || res?.data?.results || res?.results || [];
    return results
      .map((r) => ({
        name: r?.name || '',
        address: r?.formatted_address || '',
        lat: r?.geometry?.location?.lat,
        lng: r?.geometry?.location?.lng,
      }))
      .filter((r) => (r.name || r.address) && typeof r.lat === 'number' && typeof r.lng === 'number');
  },

  /* Multipart create. coverFile (a File) wins over coverImg (an Unsplash URL);
     location is GeoJSON [lng, lat]; spots is a number or blank (no limit), approval false. */
  createEvent: ({ eventName, eventType, eventStart, eventEnd, hosts, locationName, location, description, spots, networkId, coverImg, coverFile }) => {
    const fd = new FormData();
    fd.append('eventName', eventName);
    if (eventType) fd.append('eventType', eventType);
    fd.append('eventStart', eventStart);
    fd.append('eventEnd', eventEnd);
    fd.append('hosts', JSON.stringify(hosts || []));
    fd.append('approvalRequired', 'false');
    if (locationName) fd.append('locationName', locationName);
    if (location) fd.append('location', JSON.stringify(location));
    fd.append('spots', spots || '');
    fd.append('description', description || '');
    fd.append('networkId', networkId);
    // fd.append('status', 'true');
    if (coverFile) fd.append('coverImg', coverFile);
    else if (coverImg) fd.append('coverImg', coverImg);
    return apiClient.post(ENDPOINTS.ROOM_PARENT_ADMIN.EVENT_CREATE, fd);
  },

  deleteEvent: (eventId) => apiClient.delete(ENDPOINTS.ROOM_PARENT_ADMIN.EVENT_BY_ID(eventId)),

  /* status true = event sent/visible to parents (drives the Send to Parents button). */
  setEventStatus: (eventId, status) =>
    apiClient.patch(ENDPOINTS.ROOM_PARENT_ADMIN.EVENT_STATUS, { eventId, status }),

  /* Partial update — only appended fields change. A coverFile re-uploads the
     cover (server clears coverUrl); a coverImg URL string sets coverUrl instead.
     Objects (e.g. location GeoJSON) are JSON-stringified. */
  updateEvent: ({ eventId, ...fields }, coverFile) => {
    const fd = new FormData();
    fd.append('eventId', eventId);
    Object.entries(fields).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      fd.append(k, typeof v === 'object' ? JSON.stringify(v) : v);
    });
    if (coverFile) fd.append('coverImg', coverFile);
    return apiClient.put(ENDPOINTS.ROOM_PARENT_ADMIN.EVENT, fd);
  },

  list: (scope) => apiClient.get(ENDPOINTS.EVENTS.BASE, { params: { scope } }),
  create: (event) => apiClient.post(ENDPOINTS.EVENTS.BASE, event),
  update: (id, patch) => apiClient.put(ENDPOINTS.EVENTS.BY_ID(id), patch),
  remove: (id) => apiClient.delete(ENDPOINTS.EVENTS.BY_ID(id)),
  sendInvite: (id, subject) => apiClient.post(ENDPOINTS.EVENTS.SEND_INVITE(id), { subject }),
};

export default eventsService;
